import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, User as UserIcon, Phone, FileText, Save, Loader2,
  Wrench, ClipboardList, Briefcase,
} from "lucide-react";
import HtmlEditor from "../components/HtmlEditor";
import SolutionTypeSelector from "../components/SolutionTypeSelector";
import AddressMapPicker from "../components/AddressMapPicker";

const ORDER_TYPES = [
  { code: "instalacion", label: "Instalación", needsSalesman: true, needsSubscription: false },
  { code: "soporte",     label: "Soporte técnico", needsSalesman: false, needsSubscription: true },
  { code: "retiro",      label: "Retiro",   needsSalesman: false, needsSubscription: true },
  { code: "traslado",    label: "Traslado", needsSalesman: false, needsSubscription: true },
  { code: "upgrade",     label: "Upgrade / Cambio de plan", needsSalesman: false, needsSubscription: true },
];

export default function OrdenNueva() {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [branches, setBranches] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loadingTechs, setLoadingTechs] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [form, setForm] = useState({
    client_name: "",
    client_document: "",
    client_phone: "",
    address: "",
    type: "instalacion",
    priority: "P2",
    service: "",
    zone_id: "",
    technician_id: "",
    scheduled_at: "",
    subscription_ref: "",
    salesman_name: "",
    salesman_phone: "",
    installation_observations: "",
    description_html: "",
    observations: "",
    solution_type_id: "",
    solution_type_code: "",
    solution_type_name: "",
    // v1.9.2 · Mapa picker de dirección (CX-05)
    lat: "",
    lng: "",
  });
  const [solutionType, setSolutionType] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/v1/config/zones").then((r) => setZones(r.data.items || [])).catch(() => setZones([]));
    api.get("/v1/config/branches").then((r) => setBranches(r.data.items || [])).catch(() => setBranches([]));
  }, []);

  // Cargar técnicos filtrados por sucursal + zona
  useEffect(() => {
    if (!selectedBranchId) {
      setTechnicians([]);
      return;
    }
    setLoadingTechs(true);
    const params = new URLSearchParams();
    params.set("branch_id", selectedBranchId);
    if (form.zone_id) params.set("zone_id", form.zone_id);
    api.get(`/v1/users/technicians?${params.toString()}`)
      .then((r) => setTechnicians(r.data?.items || []))
      .catch(() => setTechnicians([]))
      .finally(() => setLoadingTechs(false));
  }, [selectedBranchId, form.zone_id]);

  // Si cambia sucursal/zona y el técnico ya no está en la lista filtrada, resetear
  useEffect(() => {
    if (form.technician_id && technicians.length > 0) {
      if (!technicians.find((t) => t.id === form.technician_id)) {
        setForm((f) => ({ ...f, technician_id: "" }));
      }
    }
    // eslint-disable-next-line
  }, [technicians]);

  // Zonas filtradas por sucursal seleccionada
  const filteredZones = useMemo(() => {
    if (!selectedBranchId) return [];
    return zones.filter((z) => z.branch_id === selectedBranchId);
  }, [zones, selectedBranchId]);

  // Al cambiar sucursal, resetear zone_id si no pertenece a la nueva sucursal
  const handleBranchChange = (branchId) => {
    setSelectedBranchId(branchId);
    if (form.zone_id) {
      const currentZone = zones.find((z) => z.id === form.zone_id);
      if (!currentZone || currentZone.branch_id !== branchId) {
        setForm((f) => ({ ...f, zone_id: "" }));
      }
    }
  };

  // v1.7.9 · Reglas efectivas del árbol jerárquico (cascada aplicada · fix QA20-07 #5)
  const [effectiveRules, setEffectiveRules] = useState(null);

  const cfg = useMemo(() => ORDER_TYPES.find((t) => t.code === form.type) || ORDER_TYPES[0], [form.type]);
  // v1.9.5 · Bug QA 22-07 #5: Vendedor sólo obligatorio en INSTALACIONES NUEVAS
  // Además del type, revisar el nombre del tipo de solución elegido: si contiene
  // MANTENIMIENTO / RETIRO / TRASLADO / SOPORTE / ASISTENCIA / INSPECCIÓN → vendedor NO obligatorio
  const isInstall = useMemo(() => {
    // Si el order type NO requiere vendedor (soporte, retiro, etc.), no es instalación
    if (!cfg.needsSalesman) return false;
    // Si el tipo de solución elegido pertenece a un flujo NO-instalación, tampoco lo es
    const solName = (solutionType?.name || form.solution_type_name || "").toUpperCase();
    const parentType = (solutionType?.parent_type || "").toLowerCase();
    const pathStr = ((effectiveRules?.path || []).join(" ") || "").toUpperCase();
    const NON_INSTALL_KEYWORDS = /MANTENIMIENTO|RETIRO|TRASLADO|SOPORTE|ASISTENCIA|INSPECCION|INSPECCIÓN|MIGRACI|CAMBIO|REVISION|REVISIÓN|DIAGN[ÓO]STICO|LIMPIEZA|REPARACI[ÓO]N/;
    const NON_INSTALL_PARENTS = /mantenimiento|retiro|soporte|traslado|asistencia|inspeccion|cambio_equipo|devolver_equipo|revision|diagnostico|reparacion/;
    if (NON_INSTALL_KEYWORDS.test(solName) || NON_INSTALL_KEYWORDS.test(pathStr)) return false;
    if (NON_INSTALL_PARENTS.test(parentType)) return false;
    return true;
  }, [cfg, solutionType, form.solution_type_name, effectiveRules]);

  // Cuando cambia el tipo de solución, si su parent_type mapea a un tipo válido, sincronizar `type`
  const handleSolutionType = async (sol) => {
    setSolutionType(sol);
    if (!sol) {
      setForm({ ...form, solution_type_id: "", solution_type_code: "", solution_type_name: "" });
      setEffectiveRules(null);
      return;
    }
    // Mapear parent_type → type
    const parentToType = {
      instalacion: "instalacion",
      traslado: "traslado",
      retiro: "retiro",
      devolver_equipo: "retiro",
      cambio_equipo: "upgrade",
      mantenimiento_correctivo: "soporte",
      mantenimiento_preventivo: "soporte",
      asistencia_mayor: "soporte",
      asistencia_menor: "soporte",
      inspeccion_tecnica: "soporte",
    };
    const mappedType = parentToType[sol.parent_type] || form.type;
    setForm({
      ...form,
      solution_type_id: sol.id,
      solution_type_code: sol.code,
      solution_type_name: sol.name,
      type: mappedType,
    });

    // v1.7.9 · Aplicar SLA/reglas efectivas del árbol jerárquico (fix QA20-07 #5)
    // La cascada se resuelve en backend: /solution-tree/{id}/effective-rules
    try {
      const { data } = await api.get(`/v1/solution-tree/${sol.id}/effective-rules`);
      const r = data?.rules || {};
      // El priority puede quedar en su valor actual, pero las horas SLA vienen del nodo
      // Guardamos las reglas efectivas en el form para uso en el submit + panel visual
      setEffectiveRules(data);
    } catch {
      setEffectiveRules(null);
    }
  };

  const submit = async (e, opts = {}) => {
    if (e?.preventDefault) e.preventDefault();
    // Validaciones específicas para instalación
    if (isInstall && !form.salesman_name.trim()) {
      toast.error("Para instalaciones el nombre del vendedor es obligatorio"); return;
    }
    setSaving(true);
    try {
      // Limpiar campos que no aplican
      const payload = { ...form };
      if (!isInstall) {
        delete payload.salesman_name;
        delete payload.salesman_phone;
        delete payload.installation_observations;
      }
      // Campos opcionales: eliminar si están vacíos
      if (!payload.technician_id) delete payload.technician_id;
      // GPS: enviar como lat/lon (backend usa 'lon')
      if (payload.lat === "" || payload.lat == null) {
        delete payload.lat;
      } else {
        payload.lat = Number(payload.lat);
      }
      if (payload.lng === "" || payload.lng == null) {
        delete payload.lng;
      } else {
        payload.lon = Number(payload.lng);
        delete payload.lng;
      }
      if (!payload.scheduled_at) {
        delete payload.scheduled_at;
      } else {
        payload.scheduled_at = new Date(payload.scheduled_at).toISOString();
      }
      // Zone bounds override (si el agendador confirmó con motivo)
      if (opts.ackOutOfBounds) {
        payload.zone_out_of_bounds_ack = true;
        payload.zone_out_of_bounds_reason = opts.reason || "Aprobado por agendador";
      }
      const { data } = await api.post("/v1/work-orders", payload);
      toast.success(`Orden ${data.code} creada`);
      navigate(`/ordenes/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Manejo específico ZONE_OUT_OF_BOUNDS (Feb 2026)
      if (detail && typeof detail === "object" && detail.code === "ZONE_OUT_OF_BOUNDS") {
        const reason = window.prompt(
          `⚠️ La dirección del cliente está a ${detail.distance_km ?? "?"} km del borde de la zona seleccionada.\n\n` +
          `Escribe el MOTIVO para aprobar esta orden fuera del área operativa (queda registrado en auditoría). ` +
          `Deja vacío para cancelar.`
        );
        if (reason && reason.trim()) {
          setSaving(false);
          await submit(null, { ackOutOfBounds: true, reason: reason.trim() });
          return;
        } else {
          toast.info("Creación cancelada. Corrige la dirección o cambia de zona.");
        }
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Error al crear la orden"));
      }
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <Layout title="Nueva orden de trabajo" subtitle="M1 · Creación manual">
      <div className="max-w-4xl">
        <button onClick={() => navigate("/ordenes")} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4" data-testid="btn-back-orders">
          <ArrowLeft size={14} /> Volver a Órdenes
        </button>

        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg p-6 space-y-6" data-testid="new-order-form">
          {/* ── Cliente ─────────────────────────────────────────────── */}
          <Section title="Datos del cliente" icon={UserIcon}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombres completos *">
                <input required value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="input-field" data-testid="client-name" placeholder="Ej: María Isabel González Vera" />
              </Field>
              <Field label="Cédula / RUC">
                <input value={form.client_document} onChange={(e) => set("client_document", e.target.value)} className="input-field" data-testid="client-doc" />
              </Field>
              <Field label="Móvil del cliente *">
                <input required value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} className="input-field" placeholder="Ej: 0987654321" data-testid="client-phone" />
              </Field>
              <Field label="Suscripción vinculada">
                <input
                  value={form.subscription_ref}
                  onChange={(e) => set("subscription_ref", e.target.value)}
                  className="input-field"
                  placeholder="Ej: SUB-2026-000123"
                  data-testid="subscription-ref"
                />
                <p className="text-[10px] text-gray-500 mt-1">Referencia del contrato o suscripción existente si aplica.</p>
              </Field>
            </div>
          </Section>

          {/* ── Servicio ───────────────────────────────────────────── */}
          <Section title="Tipo de servicio" icon={ClipboardList}>
            <div className="mb-4">
              <label className="block text-[10px] font-medium text-gray-700 mb-1 uppercase tracking-wider font-mono">
                Tipo de Solución (Categoría → Tipo → Solución)
              </label>
              <SolutionTypeSelector value={solutionType} onChange={handleSolutionType} />
            </div>
            {/* v1.7.9 · Banner de reglas efectivas del árbol (cascada aplicada) */}
            {effectiveRules && (
              <div className="mb-4 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-[11px]" data-testid="effective-rules-banner">
                <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-800 mb-1.5">
                  ✓ Reglas efectivas para {effectiveRules.name} · cascada N-nivel
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-emerald-900">
                  <div>SLA P1: <b>{effectiveRules.rules.sla_p1_hours}h</b></div>
                  <div>SLA P2: <b>{effectiveRules.rules.sla_p2_hours}h</b></div>
                  <div>SLA P3: <b>{effectiveRules.rules.sla_p3_hours}h</b></div>
                  <div>Tecnología: <b>{effectiveRules.rules.tecnologia}</b></div>
                  <div>Agenda: <b>{effectiveRules.rules.scheduling_mode}</b></div>
                  <div>IA+GPS: <b>{effectiveRules.rules.ai_gps_validation ? "Sí" : "No"}</b></div>
                  <div>Foto: <b>{effectiveRules.rules.photo_report_required ? "Sí" : "No"}</b></div>
                  <div>PVP editable: <b>{effectiveRules.rules.pvp_configurable ? "Sí" : "No"}</b></div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Tipo de orden *">
                <select required value={form.type} onChange={(e) => set("type", e.target.value)} className="input-field" data-testid="order-type">
                  {ORDER_TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Prioridad *">
                <select required value={form.priority} onChange={(e) => set("priority", e.target.value)} className="input-field" data-testid="order-priority">
                  <option value="P1">P1 — Alta (SLA {effectiveRules?.rules?.sla_p1_hours ?? 4}h)</option>
                  <option value="P2">P2 — Media (SLA {effectiveRules?.rules?.sla_p2_hours ?? 24}h)</option>
                  <option value="P3">P3 — Baja (SLA {effectiveRules?.rules?.sla_p3_hours ?? 72}h)</option>
                </select>
              </Field>
              <Field label="Plan / Servicio">
                <input value={form.service} onChange={(e) => set("service", e.target.value)} className="input-field" placeholder="Ej: FTTH 300 Mbps" data-testid="service" />
              </Field>
              {effectiveRules?.rules?.pvp_configurable && (
                <Field label="PVP cliente (USD)">
                  <input type="number" step="0.01" min="0" value={form.pvp_amount || ""}
                    onChange={(e) => set("pvp_amount", e.target.value === "" ? "" : Number(e.target.value))}
                    className="input-field font-mono text-right"
                    placeholder="0.00" data-testid="order-pvp-amount" />
                </Field>
              )}
            </div>
          </Section>

          {/* ── Vendedor (solo instalación) ────────────────────────── */}
          {isInstall && (
            <Section title="Datos del vendedor (obligatorio en instalaciones)" icon={Briefcase}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nombre del vendedor *">
                  <input
                    required={isInstall}
                    value={form.salesman_name}
                    onChange={(e) => set("salesman_name", e.target.value)}
                    className="input-field"
                    placeholder="Ej: Carlos Ramírez"
                    data-testid="salesman-name"
                  />
                </Field>
                <Field label="Móvil del vendedor">
                  <input
                    value={form.salesman_phone}
                    onChange={(e) => set("salesman_phone", e.target.value)}
                    className="input-field"
                    placeholder="Ej: 0999888777"
                    data-testid="salesman-phone"
                  />
                </Field>
              </div>
            </Section>
          )}

          {/* ── Ubicación con mapa picker (v1.9.2 · CX-05) ─────────── */}
          <Section title="Ubicación con marcador en el mapa" icon={MapPin}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
              <Field label="Sucursal" required>
                <select
                  value={selectedBranchId}
                  onChange={(e) => handleBranchChange(e.target.value)}
                  className="input-field"
                  data-testid="branch"
                >
                  <option value="">— Seleccionar sucursal —</option>
                  {branches.filter((b) => b.active !== false).map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Zona operativa">
                <select
                  value={form.zone_id}
                  onChange={(e) => set("zone_id", e.target.value)}
                  className="input-field disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  disabled={!selectedBranchId}
                  data-testid="zone"
                >
                  <option value="">
                    {!selectedBranchId
                      ? "Elija sucursal primero"
                      : filteredZones.length === 0
                        ? "— Sin zonas configuradas —"
                        : "— Seleccionar zona —"}
                  </option>
                  {filteredZones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
                {selectedBranchId && filteredZones.length === 0 && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    Esta sucursal aún no tiene zonas asignadas.
                  </p>
                )}
              </Field>
              <Field label="Coordenadas GPS">
                <div className="flex gap-1">
                  <input
                    type="number" step="any"
                    value={form.lat}
                    onChange={(e) => set("lat", e.target.value)}
                    placeholder="Latitud"
                    className="input-field font-mono text-xs"
                    data-testid="order-lat"
                  />
                  <input
                    type="number" step="any"
                    value={form.lng}
                    onChange={(e) => set("lng", e.target.value)}
                    placeholder="Longitud"
                    className="input-field font-mono text-xs"
                    data-testid="order-lng"
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-1">Se auto-completa al marcar el mapa</p>
              </Field>
            </div>
            <Field label="Dirección completa *">
              <AddressMapPicker
                address={form.address}
                onAddressChange={(v) => set("address", v)}
                lat={form.lat}
                lng={form.lng}
                onLocationChange={({ lat, lng }) => {
                  setForm((f) => ({ ...f, lat: String(lat), lng: String(lng) }));
                }}
                zoneGeometry={(() => {
                  const z = zones.find((x) => x.id === form.zone_id);
                  return z?.geometry;
                })()}
                zoneType={(() => {
                  const z = zones.find((x) => x.id === form.zone_id);
                  return z?.geometry_type;
                })()}
                height={320}
              />
            </Field>
          </Section>

          {/* ── Asignación técnica (opcional en creación) ────────────── */}
          <Section title="Asignación técnica (opcional)" icon={UserIcon}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Técnico asignado">
                <select
                  value={form.technician_id}
                  onChange={(e) => set("technician_id", e.target.value)}
                  className="input-field disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  disabled={!selectedBranchId}
                  data-testid="technician"
                >
                  <option value="">
                    {!selectedBranchId
                      ? "Elija sucursal primero"
                      : loadingTechs
                        ? "Cargando técnicos..."
                        : technicians.length === 0
                          ? "— Sin técnicos disponibles —"
                          : "— Sin asignar (agendar después) —"}
                  </option>
                  {technicians.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} · {t.role_display || t.role}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-gray-500 mt-1">
                  {form.zone_id
                    ? `Técnicos de la sucursal + zona seleccionada (${technicians.length})`
                    : selectedBranchId
                      ? `Todos los técnicos de la sucursal (${technicians.length})`
                      : "Selecciona una sucursal para ver técnicos disponibles"}
                </p>
              </Field>
              <Field label="Fecha planificada">
                <input
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => set("scheduled_at", e.target.value)}
                  className="input-field"
                  data-testid="scheduled-at"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Deja vacío si se agenda automáticamente por SLA.
                </p>
              </Field>
            </div>
          </Section>

          {/* ── Observaciones instalación (condicional) ────────────── */}
          {isInstall && (
            <Section title="Observaciones para la instalación" icon={Wrench}>
              <textarea
                value={form.installation_observations}
                onChange={(e) => set("installation_observations", e.target.value)}
                rows={2}
                className="input-field"
                placeholder="Notas específicas para el técnico: hora preferida, acceso al inmueble, coordinación previa, etc."
                data-testid="installation-observations"
              />
            </Section>
          )}

          {/* ── Descripción HTML ────────────────────────────────────── */}
          <Section title="Descripción técnica" icon={FileText}>
            <HtmlEditor
              value={form.description_html}
              onChange={(v) => set("description_html", v)}
              placeholder="Descripción con formato para el técnico: materiales requeridos, pasos técnicos, alertas de seguridad, referencias visuales, etc."
              testId="description-html"
              minRows={6}
            />
            <p className="text-[11px] text-gray-500 mt-1">Puedes aplicar formato (negrita, listas, enlaces). Este contenido se muestra al técnico en el detalle de la orden.</p>
          </Section>

          {/* ── Observaciones generales ─────────────────────────────── */}
          <Section title="Observaciones generales">
            <textarea
              value={form.observations}
              onChange={(e) => set("observations", e.target.value)}
              rows={2}
              className="input-field"
              placeholder="Notas adicionales generales (opcional)"
              data-testid="observations"
            />
          </Section>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/ordenes")}
              className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50"
              data-testid="btn-cancel-order"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 flex items-center gap-2"
              data-testid="submit-new-order"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Crear orden
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input-field { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.875rem; background: white; }
        .input-field:focus { outline: none; border-color: #E0E621; box-shadow: 0 0 0 2px rgba(200, 230, 0, 0.2); }
      `}</style>
    </Layout>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wider" style={{ fontFamily: "Chivo" }}>
        {Icon && <Icon size={15} className="text-[#0A3747]" />}
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-medium text-gray-700 mb-1 uppercase tracking-wider font-mono">{label}</label>
      {children}
    </div>
  );
}
